import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { AuthService } from '@auth/services/auth.service';
import { producerUserId } from '@auth/utils/grants';
import { environment } from '@environment/environment';

import { ApiService, RequestMethod } from './api.service';
import { LogService } from './log.service';
import { NotificationsService } from './notifications.service';
import { ProducerService } from './producer.service';

/**
 * Materializes the producer identity facet (AME.ProducerUser, keyed by the Entra oid) on first sign-in and re-mints
 * the token so its `producer-user:{id}` self grant lands before any producer call. The facet is born at onboarding,
 * not at issuance, so a brand-new sign-in's token carries no producer grant until this bootstrap runs (MLP-1512).
 */
@Injectable({ providedIn: 'root' })
export class ProducerIdentityService {
  private readonly authService = inject(AuthService);
  private readonly apiService = inject(ApiService);
  private readonly logService = inject(LogService);
  private readonly notificationsService = inject(NotificationsService);
  private readonly producerService = inject(ProducerService);

  private readonly amApiBase = environment.apis.accessManagementEngine;

  // The bootstrap running now, which concurrent callers share, so two rapid authenticated effect runs do not both
  // materialize the facet and re-mint.
  private inFlight: Promise<void> | null = null;

  // The producer id the producer record was last loaded for, so a re-sign-in as a different producer reloads it
  // rather than read the previous producer's cached record.
  private producerLoadedForId: string | null = null;

  // The ready sequence running now, which concurrent callers share, so the app-shell effect and a route guard both
  // firing at load do not each load the producer record (two reads of the same two endpoints).
  private readyInFlight: Promise<void> | null = null;

  /**
   * Ensures the signed-in producer's facet grant is on the token, materializing the facet and re-minting if not. A
   * no-op once the grant is present, so it is safe to call on every authenticated effect run. Never throws: a failed
   * bootstrap is surfaced to the producer and left for the next sign-in to retry.
   */
  ensureFacetBootstrapped(): Promise<void> {
    this.inFlight ??= this.run().finally(() => {
      this.inFlight = null;
    });
    return this.inFlight;
  }

  /**
   * The single sign-in→ready sequence both the app shell and the route guards await: materialize the facet + re-mint
   * (so producer calls are authorized), then load the producer record. Idempotent — the facet step is a no-op once
   * the grant is present, and the producer read is served from the service's cache unless this is a new producer
   * identity (then it is forced to reload). Never throws, so a guard can await it directly.
   */
  ensureProducerReady(): Promise<void> {
    // Share one run across concurrent callers (the app shell + a route guard at load), then clear so a later caller
    // re-runs — served from the service cache unless the identity changed.
    this.readyInFlight ??= this.loadProducerReady().finally(() => {
      this.readyInFlight = null;
    });
    return this.readyInFlight;
  }

  private async loadProducerReady(): Promise<void> {
    await this.ensureFacetBootstrapped();

    const id = this.authService.authUser()?.id ?? null;
    const force = id !== this.producerLoadedForId;
    if (force && this.producerLoadedForId !== null) {
      // The signed-in identity changed (sign-out → sign-in as a different producer): drop the previous producer's
      // cached record before the reload. Otherwise the producer self-read reuses the previous producer's cached guid,
      // and a fresh producer with no record of their own is left reading the previous producer's status.
      this.producerService.clearIdentityCache();
    }
    try {
      await firstValueFrom(this.producerService.getProducerAndProducerUser(force));
      this.producerLoadedForId = id;
    } catch (error) {
      this.logService.error('[ProducerIdentityService] failed to load the producer record', { error });
    }
  }

  private async run(): Promise<void> {
    const token = this.authService.accessToken();
    if (!token) return;

    if (this.hasFacetGrant(token)) {
      // The facet grant is already on the token; nothing to materialize or re-mint.
      return;
    }

    const externalId = this.authService.authUser()?.id;
    if (!externalId) return;

    this.logService.info('[ProducerIdentityService] materializing the producer facet', { externalId });
    try {
      await firstValueFrom(
        this.apiService.request<null>(
          RequestMethod.PUT,
          `${this.amApiBase}/v1/producers/users/external/${externalId}`,
          'ProducerIdentity.Bootstrap_Error',
          {},
          true
        )
      );
    } catch (error) {
      // A failed bootstrap must not re-mint as if it succeeded, nor loop: surface it and leave the token as is. The
      // next sign-in (or a retry) attempts it again.
      this.logService.error('[ProducerIdentityService] facet bootstrap failed', { error });
      this.notificationsService.addError('We could not finish setting up your producer account. Please try again.');
      return;
    }

    // Re-mint so the newly materialized facet's self grant is embedded in the token. refreshClaims forces a fresh
    // token (bypassing the MSAL cache) so issuance re-runs and the grant lands.
    await this.authService.refreshClaims();

    if (!this.hasFacetGrant(this.authService.accessToken())) {
      this.logService.warn('[ProducerIdentityService] facet grant still absent after bootstrap and re-mint');
    }
  }

  // The producer facet grant is an owner-role grant over `producer-user:{id}`; its presence means the AME.ProducerUser
  // facet is materialized and the caller may make producer calls. A malformed token reads as no grant (then bootstrap).
  private hasFacetGrant(token: string | null): boolean {
    return producerUserId(token) !== null;
  }
}
